<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import IntegrationsAPI from 'dashboard/api/integrations';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  hook: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saved']);

const buildProfiles = settings => {
  const profiles = { ...(settings.module_mappings || {}) };
  const activeModule = settings.module;

  if (activeModule && !profiles[activeModule]) {
    profiles[activeModule] = {
      assignment_field: settings.assignment_field || '',
      field_mappings: settings.field_mappings || {},
      field_configuration: settings.field_configuration || {},
    };
  }

  return profiles;
};

const store = useStore();
const { t } = useI18n();
const modules = ref([]);
const fields = ref([]);
const profiles = ref(buildProfiles(props.hook.settings));
const activeModule = ref(props.hook.settings.module || '');
const selectedModule = ref(activeModule.value);
const moduleToConfigure = ref('');
const moduleSearch = ref('');
const fieldSearch = ref('');
const modulesLoaded = ref(false);
const isLoadingModules = ref(false);
const isLoadingFields = ref(false);
const isSaving = ref(false);
const syncAutomatically = ref(true);

const standardMappingDefinitions = computed(() => [
  { key: 'first_name', label: t('INTEGRATION_APPS.VTIGER.MAPPING.FIRST_NAME') },
  { key: 'last_name', label: t('INTEGRATION_APPS.VTIGER.MAPPING.LAST_NAME') },
  { key: 'email', label: t('INTEGRATION_APPS.VTIGER.MAPPING.EMAIL') },
  { key: 'phone', label: t('INTEGRATION_APPS.VTIGER.MAPPING.PHONE') },
  { key: 'mobile', label: t('INTEGRATION_APPS.VTIGER.MAPPING.MOBILE') },
  { key: 'company', label: t('INTEGRATION_APPS.VTIGER.MAPPING.COMPANY') },
  {
    key: 'lead_source',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.LEAD_SOURCE'),
  },
  {
    key: 'lead_status',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.LEAD_STATUS'),
  },
  { key: 'country', label: t('INTEGRATION_APPS.VTIGER.MAPPING.COUNTRY') },
  { key: 'branch', label: t('INTEGRATION_APPS.VTIGER.MAPPING.BRANCH') },
  {
    key: 'executive_data',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.EXECUTIVE_DATA'),
  },
  { key: 'city', label: t('INTEGRATION_APPS.VTIGER.MAPPING.CITY') },
  { key: 'comments', label: t('INTEGRATION_APPS.VTIGER.MAPPING.COMMENTS') },
  {
    key: 'assigned_user',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.ASSIGNED_USER'),
    required: true,
  },
]);

const appointmentMappingDefinitions = computed(() => [
  { key: 'first_name', label: t('INTEGRATION_APPS.VTIGER.MAPPING.FIRST_NAME') },
  { key: 'last_name', label: t('INTEGRATION_APPS.VTIGER.MAPPING.LAST_NAME') },
  {
    key: 'email',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.EMAIL'),
    required: true,
  },
  { key: 'phone', label: t('INTEGRATION_APPS.VTIGER.MAPPING.PHONE') },
  { key: 'mobile', label: t('INTEGRATION_APPS.VTIGER.MAPPING.MOBILE') },
  {
    key: 'lead_source',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.LEAD_SOURCE'),
  },
  { key: 'address', label: t('INTEGRATION_APPS.VTIGER.MAPPING.ADDRESS') },
  {
    key: 'assigned_user',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.ASSIGNED_USER'),
    required: true,
  },
  {
    key: 'appointment_status',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_STATUS'),
    required: true,
  },
  {
    key: 'appointment_time',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_TIME'),
    required: true,
  },
  {
    key: 'appointment_date',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_DATE'),
    required: true,
  },
  {
    key: 'appointment_type',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_TYPE'),
    required: true,
  },
  {
    key: 'appointment_description',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_DESCRIPTION'),
    required: true,
  },
  {
    key: 'appointment_meeting_url',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_MEETING_URL'),
    required: true,
  },
  {
    key: 'appointment_generated_by',
    label: t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_GENERATED_BY'),
    required: true,
  },
]);

const mappingDefinitions = computed(() =>
  selectedModule.value === 'Contacts'
    ? appointmentMappingDefinitions.value
    : standardMappingDefinitions.value
);

const fieldMappings = reactive({});
const fieldConfigurations = reactive({});
const selectedModuleDetails = computed(() =>
  modules.value.find(item => item.name === selectedModule.value)
);
const configuredModules = computed(() =>
  Object.keys(profiles.value).map(name => {
    const details = modules.value.find(item => item.name === name);
    return { name, label: details?.label || name };
  })
);
const filteredModules = computed(() => {
  const query = moduleSearch.value.trim().toLocaleLowerCase();
  if (!query) return modules.value;

  return modules.value.filter(item =>
    `${item.label} ${item.name}`.toLocaleLowerCase().includes(query)
  );
});
const isSelectedModuleActive = computed(
  () => selectedModule.value === activeModule.value
);
const isAppointmentSyncEnabled = computed(
  () => props.hook.settings.enable_appointment_sync === true
);
const hasMissingRequiredMappings = computed(() =>
  mappingDefinitions.value.some(
    definition => definition.required && !fieldMappings[definition.key]
  )
);
const configurableCrmFields = computed(() => fields.value);
const filteredConfigurableCrmFields = computed(() => {
  const query = fieldSearch.value.trim().toLocaleLowerCase();
  if (!query) return configurableCrmFields.value;

  return configurableCrmFields.value.filter(field =>
    `${field.label} ${field.name}`.toLocaleLowerCase().includes(query)
  );
});
const crmFieldGroups = computed(() => [
  {
    key: 'required',
    title: t('INTEGRATION_APPS.VTIGER.MAPPING.MANDATORY_FIELDS_COUNT', {
      count: configurableCrmFields.value.filter(field => field.mandatory)
        .length,
    }),
    help: t('INTEGRATION_APPS.VTIGER.MAPPING.MANDATORY_FIELDS_HELP'),
    fields: filteredConfigurableCrmFields.value.filter(
      field => field.mandatory
    ),
    isOpen: true,
  },
  {
    key: 'optional',
    title: t('INTEGRATION_APPS.VTIGER.MAPPING.OPTIONAL_FIELDS_COUNT', {
      count: configurableCrmFields.value.filter(field => !field.mandatory)
        .length,
    }),
    help: t('INTEGRATION_APPS.VTIGER.MAPPING.OPTIONAL_FIELDS_HELP'),
    fields: filteredConfigurableCrmFields.value.filter(
      field => !field.mandatory
    ),
    isOpen: Boolean(fieldSearch.value.trim()),
  },
]);
const fieldRequiresFixedValue = field =>
  field.mandatory ||
  mappingDefinitions.value.some(
    definition =>
      definition.required && fieldMappings[definition.key] === field.name
  );
const hasInvalidFixedFields = computed(() =>
  configurableCrmFields.value.some(field => {
    const configuration = fieldConfigurations[field.name];
    return (
      fieldRequiresFixedValue(field) &&
      configuration?.hidden &&
      !configuration.fixed_value?.toString().trim()
    );
  })
);
const hasDuplicateMappings = computed(() => {
  const mappedFields = Object.values(fieldMappings).filter(Boolean);
  return new Set(mappedFields).size !== mappedFields.length;
});
const fieldMappingTitle = computed(() =>
  selectedModule.value === 'Contacts'
    ? t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_FIELD_MAPPING')
    : t('INTEGRATION_APPS.VTIGER.MAPPING.FIELD_MAPPING')
);
const fieldMappingHelp = computed(() =>
  selectedModule.value === 'Contacts'
    ? t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_FIELD_MAPPING_HELP')
    : t('INTEGRATION_APPS.VTIGER.MAPPING.FIELD_MAPPING_HELP')
);

const mappedChatwootKey = fieldName =>
  Object.keys(fieldMappings).find(key => fieldMappings[key] === fieldName) ||
  '';

const mappingUsedByAnotherField = (mappingKey, fieldName) =>
  Boolean(fieldMappings[mappingKey]) && fieldMappings[mappingKey] !== fieldName;

const setCrmFieldMapping = (fieldName, mappingKey) => {
  const previousKey = mappedChatwootKey(fieldName);
  if (previousKey) fieldMappings[previousKey] = '';

  if (!mappingKey) return;

  Object.keys(fieldMappings).forEach(key => {
    if (fieldMappings[key] === fieldName || key === mappingKey) {
      fieldMappings[key] = '';
    }
  });
  fieldMappings[mappingKey] = fieldName;

  const configuration = fieldConfigurations[fieldName];
  if (configuration) {
    configuration.hidden = false;
    configuration.fixed_value = '';
  }
};

const setFieldHidden = (fieldName, hidden) => {
  const configuration = fieldConfigurations[fieldName];
  configuration.hidden = hidden;
  if (!hidden) {
    configuration.fixed_value = '';
  }
};

const configureFieldConfigurations = () => {
  const savedConfigurations =
    profiles.value[selectedModule.value]?.field_configuration || {};
  const availableNames = new Set(fields.value.map(field => field.name));

  Object.keys(fieldConfigurations).forEach(key => {
    if (!availableNames.has(key)) delete fieldConfigurations[key];
  });

  fields.value.forEach(field => {
    const saved = savedConfigurations[field.name] || {};
    const hasSavedConfiguration = Object.prototype.hasOwnProperty.call(
      savedConfigurations,
      field.name
    );
    const hasSavedMapping = Boolean(mappedChatwootKey(field.name));
    fieldConfigurations[field.name] = {
      hidden: hasSavedConfiguration
        ? Boolean(saved.hidden)
        : !field.mandatory && !hasSavedMapping,
      fixed_value: saved.fixed_value ?? '',
    };

  });
};

const configureMappings = () => {
  const savedMappings =
    profiles.value[selectedModule.value]?.field_mappings || {};
  const availableNames = fields.value.map(field => field.name);

  Object.keys(fieldMappings).forEach(key => delete fieldMappings[key]);

  mappingDefinitions.value.forEach(({ key }) => {
    const hasSavedMapping = Object.prototype.hasOwnProperty.call(
      savedMappings,
      key
    );
    const preferredField = hasSavedMapping ? savedMappings[key] : '';
    fieldMappings[key] = availableNames.includes(preferredField)
      ? preferredField
      : '';
  });
  configureFieldConfigurations();
};

const errorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  t('INTEGRATION_APPS.VTIGER.MAPPING.ERROR');

const loadFields = async () => {
  if (!selectedModule.value) return;
  if (selectedModule.value === 'Contacts' && !isAppointmentSyncEnabled.value) {
    fields.value = [];
    return;
  }

  isLoadingFields.value = true;
  try {
    const { data } = await IntegrationsAPI.getVtigerModuleFields(
      props.hook.id,
      selectedModule.value
    );
    fields.value = data.fields || [];
    configureMappings();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoadingFields.value = false;
  }
};

const serializeFieldConfiguration = () =>
  configurableCrmFields.value.reduce((result, field) => {
    const configuration = fieldConfigurations[field.name];
    if (!configuration) return result;

    const hidden = Boolean(configuration.hidden);
    const fixedValue = hidden
      ? configuration.fixed_value?.toString().trim() || ''
      : '';
    if (!hidden && field.mandatory) return result;

    result[field.name] = {
      hidden,
      fixed_value: fixedValue,
    };
    return result;
  }, {});

const selectModule = async moduleName => {
  if (!moduleName) return;

  selectedModule.value = moduleName;
  syncAutomatically.value =
    moduleName === activeModule.value || !activeModule.value;
  await loadFields();
};

const loadModules = async () => {
  isLoadingModules.value = true;
  try {
    const { data } = await IntegrationsAPI.getVtigerModules(props.hook.id);
    modules.value = data.modules || [];
    modulesLoaded.value = true;

    const initialModule = modules.value.some(
      item => item.name === selectedModule.value
    )
      ? selectedModule.value
      : modules.value[0]?.name;
    await selectModule(initialModule);
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoadingModules.value = false;
  }
};

onMounted(loadModules);

const addModule = async () => {
  if (!moduleToConfigure.value) return;

  await selectModule(moduleToConfigure.value);
  moduleToConfigure.value = '';
};

const saveMapping = async () => {
  if (
    hasMissingRequiredMappings.value ||
    hasInvalidFixedFields.value ||
    hasDuplicateMappings.value ||
    isSaving.value
  ) {
    return;
  }

  isSaving.value = true;
  try {
    const profile = {
      assignment_field: fieldMappings.assigned_user,
      field_mappings: { ...fieldMappings },
      field_configuration: serializeFieldConfiguration(),
    };
    const moduleMappings = {
      ...profiles.value,
      [selectedModule.value]: profile,
    };
    const settings = {
      ...props.hook.settings,
      module_mappings: moduleMappings,
    };

    if (syncAutomatically.value || !activeModule.value) {
      settings.module = selectedModule.value;
      settings.assignment_field = profile.assignment_field;
      settings.field_mappings = profile.field_mappings;
      settings.field_configuration = profile.field_configuration;
    }

    await store.dispatch('integrations/updateHook', {
      hookId: props.hook.id,
      hookData: { settings },
    });
    profiles.value = moduleMappings;
    if (settings.module === selectedModule.value) {
      activeModule.value = selectedModule.value;
      syncAutomatically.value = true;
    }
    emit('saved', settings);
    useAlert(t('INTEGRATION_APPS.VTIGER.MAPPING.SUCCESS'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-5 px-6 pb-6">
    <div>
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="!modulesLoaded"
      class="flex flex-col items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-5"
    >
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.LOAD_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.LOAD_DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        type="button"
        icon="i-lucide-download"
        :label="$t('INTEGRATION_APPS.VTIGER.MAPPING.LOAD_MODULES')"
        :is-loading="isLoadingModules"
        @click="loadModules"
      />
    </div>

    <div v-else class="grid gap-5 lg:grid-cols-[15rem_minmax(0,1fr)]">
      <aside
        class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.CONFIGURED_MODULES') }}
          </p>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.CONFIGURED_MODULES_HELP') }}
          </p>
        </div>

        <button
          v-for="module in configuredModules"
          :key="module.name"
          type="button"
          class="flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors"
          :class="
            selectedModule === module.name
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-n-weak bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="selectModule(module.name)"
        >
          <span class="truncate">{{ module.label }}</span>
          <span
            v-if="activeModule === module.name"
            class="ml-2 shrink-0 rounded-full bg-n-brand/15 px-2 py-0.5 text-[0.65rem] font-medium text-n-brand"
          >
            {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.ACTIVE') }}
          </span>
        </button>

        <p v-if="!configuredModules.length" class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.NO_MODULES_CONFIGURED') }}
        </p>

        <div class="border-t border-n-weak pt-4">
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.ADD_MODULE') }}
            <input
              v-model="moduleSearch"
              type="search"
              class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-3 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
              :placeholder="$t('INTEGRATION_APPS.VTIGER.MAPPING.SEARCH_MODULE')"
            />
            <select
              v-model="moduleToConfigure"
              class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-3 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
            >
              <option disabled value="">
                {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.SELECT_MODULE') }}
              </option>
              <option
                v-for="module in filteredModules"
                :key="module.name"
                :value="module.name"
              >
                {{ module.label }} ({{ module.name }})
              </option>
            </select>
          </label>
          <NextButton
            class="mt-3 w-full"
            faded
            slate
            type="button"
            icon="i-lucide-plus"
            :label="$t('INTEGRATION_APPS.VTIGER.MAPPING.CONFIGURE_MODULE')"
            :disabled="!moduleToConfigure || isLoadingFields"
            @click="addModule"
          />
          <NextButton
            class="mt-2 w-full"
            ghost
            slate
            type="button"
            icon="i-lucide-refresh-cw"
            :label="$t('INTEGRATION_APPS.VTIGER.MAPPING.REFRESH_MODULES')"
            :is-loading="isLoadingModules"
            @click="loadModules"
          />
        </div>
      </aside>

      <section v-if="selectedModule" class="flex min-w-0 flex-col gap-5">
        <div
          class="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
        >
          <div>
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-11"
            >
              {{
                $t('INTEGRATION_APPS.VTIGER.MAPPING.MODULE_BEING_CONFIGURED')
              }}
            </p>
            <h4 class="mt-1 text-base font-semibold text-n-slate-12">
              {{ selectedModuleDetails?.label || selectedModule }}
            </h4>
            <p class="mt-1 text-xs text-n-slate-11">{{ selectedModule }}</p>
          </div>
          <span
            v-if="isSelectedModuleActive"
            class="rounded-full bg-n-brand/15 px-3 py-1 text-xs font-medium text-n-brand"
          >
            {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.ACTIVE_DESCRIPTION') }}
          </span>
        </div>

        <div
          v-if="selectedModule === 'Contacts' && !isAppointmentSyncEnabled"
          class="rounded-xl border border-n-weak bg-n-alpha-2 p-4"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{
              $t('INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_SYNC_REQUIRED')
            }}
          </p>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              $t(
                'INTEGRATION_APPS.VTIGER.MAPPING.APPOINTMENT_SYNC_REQUIRED_HELP'
              )
            }}
          </p>
        </div>

        <template v-else>
          <div class="flex flex-col gap-3">
            <div>
              <h5 class="text-sm font-medium text-n-slate-12">
                {{ fieldMappingTitle }}
              </h5>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ fieldMappingHelp }}
              </p>
            </div>

            <input
              v-model="fieldSearch"
              type="search"
              class="!mb-1 !h-10 !w-full !rounded-lg !bg-n-alpha-3 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
              :placeholder="
                $t('INTEGRATION_APPS.VTIGER.MAPPING.SEARCH_CRM_FIELDS')
              "
            />

            <Accordion
              v-for="group in crmFieldGroups"
              :key="group.key"
              :title="group.title"
              :is-open="group.isOpen"
            >
              <p class="mb-3 text-sm text-n-slate-11">
                {{ group.help }}
              </p>

              <div class="flex flex-col gap-3">
                <article
                  v-for="field in group.fields"
                  :key="field.name"
                  class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
                >
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <p class="font-medium text-n-slate-12">
                          {{ field.label }}
                        </p>
                        <span
                          v-if="field.mandatory"
                          class="text-xs font-medium text-n-ruby-9"
                        >
                          {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.REQUIRED') }}
                        </span>
                      </div>
                      <code class="text-xs text-n-slate-11">{{
                        field.name
                      }}</code>
                    </div>
                    <span
                      class="rounded bg-n-alpha-3 px-2 py-1 text-xs text-n-slate-11"
                    >
                      {{ field.type }}
                    </span>
                  </div>

                  <label
                    class="mt-4 flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.CHATWOOT_FIELD') }}
                    <select
                      :value="mappedChatwootKey(field.name)"
                      :disabled="fieldConfigurations[field.name].hidden"
                      class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-3 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
                      @change="
                        setCrmFieldMapping(field.name, $event.target.value)
                      "
                    >
                      <option value="">
                        {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.NOT_MAPPED') }}
                      </option>
                      <option
                        v-for="definition in mappingDefinitions"
                        :key="definition.key"
                        :value="definition.key"
                        :disabled="
                          mappingUsedByAnotherField(definition.key, field.name)
                        "
                      >
                        {{ definition.label }}
                      </option>
                    </select>
                    <span class="text-xs font-normal text-n-slate-11">
                      {{
                        $t('INTEGRATION_APPS.VTIGER.MAPPING.CHATWOOT_FIELD_HELP')
                      }}
                    </span>
                  </label>

                  <label class="mt-4 flex cursor-pointer items-start gap-3">
                    <input
                      :checked="fieldConfigurations[field.name].hidden"
                      type="checkbox"
                      class="mt-0.5"
                      @change="
                        setFieldHidden(field.name, $event.target.checked)
                      "
                    />
                    <span>
                      <span class="block text-sm font-medium text-n-slate-12">
                        {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.HIDE_AND_FIX') }}
                      </span>
                      <span class="mt-1 block text-xs text-n-slate-11">
                        {{
                          $t(
                            'INTEGRATION_APPS.VTIGER.MAPPING.HIDE_AND_FIX_HELP'
                          )
                        }}
                      </span>
                    </span>
                  </label>

                  <label
                    v-if="fieldConfigurations[field.name].hidden"
                    class="mt-4 flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.FIXED_VALUE') }}
                    <select
                      v-if="field.picklist_values.length"
                      v-model="fieldConfigurations[field.name].fixed_value"
                      :required="fieldRequiresFixedValue(field)"
                      class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-3 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
                    >
                      <option disabled value="">
                        {{
                          $t(
                            'INTEGRATION_APPS.VTIGER.MAPPING.SELECT_FIXED_VALUE'
                          )
                        }}
                      </option>
                      <option
                        v-for="value in field.picklist_values"
                        :key="value"
                        :value="value"
                      >
                        {{ value }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model="fieldConfigurations[field.name].fixed_value"
                      :required="fieldRequiresFixedValue(field)"
                      type="text"
                      class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-3 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
                      :placeholder="
                        $t(
                          'INTEGRATION_APPS.VTIGER.MAPPING.FIXED_VALUE_PLACEHOLDER'
                        )
                      "
                    />
                  </label>
                </article>

                <p v-if="!group.fields.length" class="text-sm text-n-slate-11">
                  {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.NO_CRM_FIELDS') }}
                </p>
              </div>
            </Accordion>
          </div>

          <label
            v-if="!isSelectedModuleActive"
            class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
          >
            <input v-model="syncAutomatically" type="checkbox" class="mt-0.5" />
            <span>
              <span class="block text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.SET_ACTIVE') }}
              </span>
              <span class="mt-1 block text-sm text-n-slate-11">
                {{ $t('INTEGRATION_APPS.VTIGER.MAPPING.SET_ACTIVE_HELP') }}
              </span>
            </span>
          </label>

          <div class="flex justify-end">
            <NextButton
              type="button"
              icon="i-lucide-save"
              :label="$t('INTEGRATION_APPS.VTIGER.MAPPING.SAVE')"
              :disabled="
                hasMissingRequiredMappings ||
                hasInvalidFixedFields ||
                hasDuplicateMappings ||
                isLoadingFields
              "
              :is-loading="isSaving"
              @click="saveMapping"
            />
          </div>
        </template>
      </section>
    </div>
  </div>
</template>
